import React from 'react';

// The review panel's whole-panel states: never checked, running, failed,
// and every finding handled.

const MODE_TITLES = { proofread: 'Proofread', full: 'Full review' };

const capitalize = text => text[0].toUpperCase() + text.slice(1);

const formatElapsed = seconds => (
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
);

const MODE_DESCRIPTIONS = {
    proofread: 'Grammar, spelling and punctuation. About 20 s.',
    full: 'Proofread, plus facts, outdated info, style and suggestions. About 2 min.',
};

// Never checked (or the last check was cancelled), nothing picked yet.
export function IdleView({ errorMsg }) {
    return (
        <div className="crp-idle">
            <div>
                <div className="crp-title">Not checked yet</div>
                <div className="crp-sub">Use Check in the toolbar.</div>
            </div>
            <div className="crp-idle-rows">
                {Object.entries(MODE_DESCRIPTIONS).map(([key, description]) => (
                    <div key={key} className="crp-idle-row">
                        <span className="crp-idle-label">{MODE_TITLES[key]}</span>
                        <span>{description}</span>
                    </div>
                ))}
            </div>
            {errorMsg && <p className="crp-error">{errorMsg}</p>}
        </div>
    );
}

const MODE_NAMES = { proofread: 'proofread', full: 'full review' };

// A check picked in the toolbar, on offer: the toolbar never starts one.
// `current` is the mode of the card's results it would replace, if any.
export function OfferView({ mode, current, errorMsg, onStart, onBack }) {
    return (
        <div className="crp-idle">
            <div>
                <div className="crp-title">
                    {current ? `Run a ${MODE_NAMES[mode]}?` : `No ${MODE_NAMES[mode]} yet`}
                </div>
                <div className="crp-sub">
                    {current ? `It replaces the current ${MODE_NAMES[current]} results.` : 'This card has no check results.'}
                </div>
            </div>
            <div className="crp-idle-rows">
                <div className="crp-idle-row">
                    <span className="crp-idle-label">{MODE_TITLES[mode]}</span>
                    <span>{MODE_DESCRIPTIONS[mode]}</span>
                </div>
            </div>
            <div className="crp-offer-actions">
                <button type="button" className="hk-btn hk-btn-sm hk-btn-primary crp-done-btn" onClick={() => onStart(mode)}>
                    <i className={`pi ${mode === 'proofread' ? 'pi-check-square' : 'pi-shield'}`} />
                    {mode === 'proofread' ? 'Start proofread' : 'Start full review'}
                </button>
                {current && (
                    <button type="button" className="hk-btn hk-btn-sm crp-done-btn" onClick={onBack}>
                        Show {MODE_NAMES[current]} results
                    </button>
                )}
            </div>
            {errorMsg && <p className="crp-error">{errorMsg}</p>}
        </div>
    );
}

export function RunningView({ mode, stage, fraction, seconds, onCancel }) {
    return (
        <div className="crp-center">
            <i className="pi pi-spin pi-spinner crp-run-spinner" />
            <div className="crp-title">{mode === 'proofread' ? 'Proofreading…' : 'Running full review…'}</div>
            <div className="crp-sub">{stage}</div>
            <div className="crp-progress">
                <div className="crp-progress-fill" style={{ width: `${Math.round(fraction * 100)}%` }} />
            </div>
            <div className="ne-mono crp-elapsed">{formatElapsed(seconds)}</div>
            <button type="button" className="hk-btn hk-btn-sm crp-cancel" onClick={onCancel}>Cancel</button>
        </div>
    );
}

export function ErrorView({ mode, onRetry }) {
    return (
        <div className="crp-center">
            <i className="pi pi-exclamation-circle crp-done-icon crp-failed-icon" />
            <div className="crp-title">{MODE_TITLES[mode]} failed</div>
            <div className="crp-sub">The check stopped on the server.</div>
            <div className="crp-done-actions">
                <button type="button" className="hk-btn hk-btn-sm crp-done-btn" onClick={onRetry}>
                    <i className="pi pi-refresh" /> Try again
                </button>
            </div>
        </div>
    );
}

const RUN_NAMES = { proofread: 'proofread', review: 'review' };

// Every finding handled. When one pass of a full review failed, the check
// is incomplete: say so and offer the retry instead of a success.
// The next card of a course check that still has open findings.
function NextCard({ mode, nextCard, otherCards, onNextCard }) {
    if (!nextCard || !onNextCard) return null;
    return (
        <div className="crp-next-card">
            <div className="crp-sub">
                {otherCards === 1 ? '1 more card has' : `${otherCards} more cards have`} open findings.
            </div>
            <button type="button" className="hk-btn hk-btn-sm crp-done-btn" onClick={() => onNextCard(nextCard)}>
                {mode === 'proofread' ? 'Next card with typos' : 'Next card with findings'}
                <i className="pi pi-arrow-right" />
            </button>
        </div>
    );
}

export function AllHandledView({
    mode, applied, dismissed, failedKind, onRunFull, onRunAgain, nextCard, otherCards, onNextCard,
}) {
    const counts = applied + dismissed ? `${applied} applied · ${dismissed} dismissed` : 'No issues found.';
    if (failedKind) {
        const passed = failedKind === 'review' ? 'proofread' : 'review';
        return (
            <div className="crp-center">
                <i className="pi pi-exclamation-triangle crp-done-icon crp-failed-icon" />
                <div className="crp-title">{MODE_TITLES[mode]} incomplete</div>
                <div className="crp-sub">The {RUN_NAMES[failedKind]} failed on the server.</div>
                <div className="crp-sub">
                    {applied + dismissed ? `${capitalize(passed)}: ${counts}` : `The ${passed} found no issues.`}
                </div>
                <div className="crp-done-actions">
                    <button type="button" className="hk-btn hk-btn-sm hk-btn-primary crp-done-btn" onClick={onRunAgain}>
                        <i className="pi pi-refresh" /> Try again
                    </button>
                </div>
                <NextCard mode={mode} nextCard={nextCard} otherCards={otherCards} onNextCard={onNextCard} />
            </div>
        );
    }
    return (
        <div className="crp-center">
            <i className="pi pi-check-circle crp-done-icon" />
            <div className="crp-title">{mode === 'proofread' ? 'Proofread complete' : 'Review complete'}</div>
            <div className="crp-sub">{counts}</div>
            <div className="crp-done-actions">
                {mode === 'proofread' && (
                    <button type="button" className="hk-btn hk-btn-sm hk-btn-primary crp-done-btn" onClick={onRunFull}>
                        <i className="pi pi-shield" /> Run full review
                    </button>
                )}
                <button type="button" className="hk-btn hk-btn-sm crp-done-btn" onClick={onRunAgain}>
                    <i className="pi pi-refresh" /> {mode === 'proofread' ? 'Proofread again' : 'Run again'}
                </button>
            </div>
            <NextCard mode={mode} nextCard={nextCard} otherCards={otherCards} onNextCard={onNextCard} />
        </div>
    );
}
